import { unzipSync, zipSync, strToU8, strFromU8 } from "fflate";
import {
  fillPlaceholders, remainingPlaceholders, mapTable, fillTable,
  fillList, rewriteParagraphs, escapeXml,
} from "./ooxml.js";

/* Filling a deck from its own template.
 *
 * The template is the client's approved deck, and everything about it stays:
 * masters, fonts, the seal on every slide, the footer, the photographs. This
 * opens the file, rewrites the text inside the placeholders, and closes it.
 * Every part it was not asked about is copied through byte for byte.
 *
 * What it will not do is decide anything. It is handed a plan and it applies
 * it. Which slide carries the aging table, and what goes in it, is the
 * business of the code that knows about invoices, not of the code that knows
 * about zip files.
 */

const slidePath = (n) => `ppt/slides/slide${n}.xml`;
const relsPath = (n) => `ppt/slides/_rels/slide${n}.xml.rels`;
const slideNumber = (path) => Number(/slide(\d+)\.xml$/.exec(path)?.[1] ?? 0);

export function slideNumbers(files) {
  return Object.keys(files)
    .filter((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p))
    .map(slideNumber)
    .sort((a, b) => a - b);
}

/* One page's worth of instructions applied to one slide's XML. Tables first:
   a table's cells are written wholesale from the rows given, so filling
   placeholders first would be work thrown away, and worse, would make a
   cleared table look filled to the check afterwards. Lists next, for the same
   reason. Whole paragraphs before single placeholders, because a rule finds
   its paragraph by the template's own wording. */
function applyPage(xml, page, deckValues) {
  let out = xml;
  for (const spec of page.tables ?? []) {
    out = mapTable(out, spec.index ?? 0, (tbl) =>
      fillTable(tbl, spec.rows ?? [], { headerRows: spec.headerRows ?? 1 }));
  }
  for (const list of page.lists ?? []) out = fillList(out, list.anchor, list.lines ?? []);
  out = rewriteParagraphs(out, page.paragraphs ?? []);
  for (const anchor of page.remove ?? []) out = removePanel(out, anchor);
  const values = { ...(deckValues ?? {}), ...(page.values ?? {}) };
  if (Object.keys(values).length) out = fillPlaceholders(out, values);
  return out;
}

/**
 * templateBytes  the .pptx as it was uploaded
 * plan
 *   .values      placeholders to fill on every slide, such as the month
 *   .slides      per template slide number, one page or a list of pages.
 *                A page is { values, tables, lists, paragraphs }:
 *                  tables      [{ index, rows, headerRows }], each row a list
 *                              of cell values, or { cells, like } to dress it
 *                              as another of the template's body rows
 *                  lists       [{ anchor, lines }], a text box of lines
 *                  paragraphs  [{ startsWith, text }], whole lines rewritten,
 *                              or removed when text is null
 *                  remove      [anchor], take off the panel whose text
 *                              includes the anchor, background and all
 *                A list of pages makes continuation slides: the first page
 *                is the template slide, and every later one is a copy of it
 *                placed straight after, the way a long aging table ran onto
 *                a second slide in July.
 *   .draft       { text } stamps that on the first slide when anything is
 *                left unfilled, so a deck that is not finished says so on
 *                its cover and not only in a list on a screen
 *
 * returns { bytes, unfilled, draft }. unfilled lists what no rule reached,
 * per page: { slide, page, position, placeholders }, where slide is the
 * template's number, page counts continuation pages from 1, and position is
 * where it sits in the finished deck. That list is the point of the bracket
 * convention: a deck that still says [Status] somewhere can be refused, or
 * marked, rather than handed to a client.
 */
export function fillDeck(templateBytes, plan = {}) {
  const files = unzipSync(new Uint8Array(templateBytes));
  const numbers = slideNumbers(files);
  if (!numbers.length) {
    throw new Error("That file has no slides in it. It may not be a PowerPoint template.");
  }

  const order = deckOrder(files) ?? numbers;
  const copies = [];
  const pagesOf = new Map();

  for (const n of numbers) {
    const raw = plan.slides?.[n];
    const pages = Array.isArray(raw) ? raw : [raw ?? {}];
    const template = strFromU8(files[slidePath(n)]);
    pagesOf.set(n, pages.map((page) => applyPage(template, page ?? {}, plan.values)));
    if (pages.length > 1) copies.push(n);
  }

  /* The copies get new part numbers above anything the template uses, and are
     put in the running order straight after the slide they continue. */
  let next = Math.max(...numbers);
  const extraParts = new Map();
  for (const n of copies) {
    const extra = pagesOf.get(n).slice(1).map(() => (next += 1));
    extraParts.set(n, extra);
  }

  for (const n of numbers) {
    const [first, ...rest] = pagesOf.get(n);
    files[slidePath(n)] = strToU8(first);
    rest.forEach((xml, i) => {
      const m = extraParts.get(n)[i];
      files[slidePath(m)] = strToU8(xml);
      if (files[relsPath(n)]) files[relsPath(m)] = strToU8(copyRels(strFromU8(files[relsPath(n)])));
    });
  }
  if (copies.length) registerCopies(files, extraParts);

  /* What is left, in the order the finished deck shows it. */
  const unfilled = [];
  let position = 0;
  for (const n of order) {
    const pages = pagesOf.get(n);
    if (!pages) continue;
    pages.forEach((xml, i) => {
      position += 1;
      const left = remainingPlaceholders(xml);
      if (!left.length) return;
      unfilled.push(pages.length > 1
        ? { slide: n, page: i + 1, position, placeholders: left }
        : { slide: n, position, placeholders: left });
    });
  }

  let draft = false;
  if (plan.draft && unfilled.length) {
    const firstSlide = order[0];
    const path = slidePath(firstSlide);
    files[path] = strToU8(stamp(strFromU8(files[path]), plan.draft.text ?? "DRAFT", slideSize(files)));
    draft = true;
  }

  return { bytes: zipSync(files, { level: 6 }), unfilled, draft };
}

/* ------------------------------------------------------------ the order */

const REL = /<Relationship\b[^>]*\/>/g;
const attr = (tag, name) => new RegExp(`\\b${name}="([^"]*)"`).exec(tag)?.[1];

/* Slide part numbers in the order the deck shows them, which is the order of
   presentation.xml's list rather than the part names. They agree in a fresh
   template and stop agreeing the first time somebody drags a slide. */
function deckOrder(files) {
  const pres = files["ppt/presentation.xml"];
  const rels = files["ppt/_rels/presentation.xml.rels"];
  if (!pres || !rels) return null;
  const target = new Map((strFromU8(rels).match(REL) ?? []).map((r) => [attr(r, "Id"), attr(r, "Target")]));
  const ids = [...strFromU8(pres).matchAll(/<p:sldId\b[^>]*\/>/g)]
    .map((m) => target.get(attr(m[0], "r:id")))
    .filter(Boolean)
    .map(slideNumber);
  return ids.length ? ids : null;
}

/* A copy's relationships are its original's, less its speaker notes and any
   comments. A notes page belongs to exactly one slide and points back at it,
   so two slides sharing one is a file PowerPoint offers to repair. */
function copyRels(xml) {
  return xml.replace(REL, (r) => (/\/(notesSlide|comments)$/.test(attr(r, "Type") ?? "") ? "" : r));
}

const SLIDE_TYPE = "application/vnd.openxmlformats-officedocument.presentationml.slide+xml";
const SLIDE_REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships/slide";

/* Tells the package the copies exist: a content type for each part, a
   relationship from the presentation to it, and a place in the slide list
   right after the slide it continues. */
function registerCopies(files, extraParts) {
  const typesPath = "[Content_Types].xml";
  const presPath = "ppt/presentation.xml";
  const relsFile = "ppt/_rels/presentation.xml.rels";
  if (!files[presPath] || !files[relsFile] || !files[typesPath]) {
    throw new Error("This template is missing the parts that list its slides, so a continuation slide cannot be added.");
  }

  let types = strFromU8(files[typesPath]);
  let rels = strFromU8(files[relsFile]);
  let pres = strFromU8(files[presPath]);

  const relTags = rels.match(REL) ?? [];
  let nextRel = Math.max(0, ...relTags.map((r) => Number(/^rId(\d+)$/.exec(attr(r, "Id") ?? "")?.[1] ?? 0))) + 1;
  let nextId = Math.max(255, ...[...pres.matchAll(/<p:sldId\b[^>]*\bid="(\d+)"/g)].map((m) => Number(m[1]))) + 1;
  const relFor = new Map(relTags.map((r) => [attr(r, "Target"), attr(r, "Id")]));

  for (const [n, parts] of extraParts) {
    const added = [];
    for (const m of parts) {
      types = types.replace(/<\/Types>\s*$/, `<Override PartName="/${slidePath(m)}" ContentType="${SLIDE_TYPE}"/></Types>`);
      const rId = `rId${nextRel++}`;
      rels = rels.replace(/<\/Relationships>\s*$/,
        `<Relationship Id="${rId}" Type="${SLIDE_REL}" Target="slides/slide${m}.xml"/></Relationships>`);
      added.push(`<p:sldId id="${nextId++}" r:id="${rId}"/>`);
    }
    const original = relFor.get(`slides/slide${n}.xml`) ?? relFor.get(`/ppt/slides/slide${n}.xml`);
    const at = new RegExp(`<p:sldId\\b[^>]*r:id="${original}"[^>]*/>`);
    if (!original || !at.test(pres)) {
      throw new Error(`Slide ${n} is not in the template's slide list, so its continuation has nowhere to go.`);
    }
    pres = pres.replace(at, (tag) => tag + added.join(""));
  }

  files[typesPath] = strToU8(types);
  files[relsFile] = strToU8(rels);
  files[presPath] = strToU8(pres);
}

/* ------------------------------------------------------------ panels */

const SHAPE = /<p:(sp|pic|graphicFrame|grpSp|cxnSp)\b[\s\S]*?<\/p:\1>/g;
const boundsOf = (shape) => {
  const m = /<a:off x="(-?\d+)" y="(-?\d+)"\/>\s*<a:ext cx="(\d+)" cy="(\d+)"/.exec(shape);
  return m ? { x: +m[1], y: +m[2], w: +m[3], h: +m[4] } : null;
};
const within = (a, b) => a.x >= b.x && a.y >= b.y && a.x + a.w <= b.x + b.w && a.y + a.h <= b.y + b.h;
const textIn = (shape) => [...shape.matchAll(/<a:t(?:\s[^>]*)?>([^<]*)<\/a:t>/g)].map((m) => m[1]).join("");

/**
 * Removes a panel from a slide: the shape whose words include `anchor`, the
 * smallest wordless shape drawn behind it (the panel's background), and
 * everything else inside that background, such as its accent bar, its icon
 * and its body text. For a box that belongs on the last page of a run only,
 * like July's aging summary, so the earlier pages do not repeat totals that
 * read as though they were the page's own.
 */
export function removePanel(xml, anchor) {
  const tree = /<p:spTree>([\s\S]*)<\/p:spTree>/.exec(xml);
  if (!tree) return xml;
  const shapes = [...tree[1].matchAll(SHAPE)].map((m) => ({ xml: m[0], box: boundsOf(m[0]), text: textIn(m[0]) }));
  const label = shapes.find((s) => s.box && s.text.includes(escapeXml(anchor)));
  if (!label) return xml;
  /* A wordless shape with a table inside it is the slide's own background,
     not a panel, and taking it would take the table too. */
  const holdsTable = (s) => shapes.some((t) => /^<p:graphicFrame/.test(t.xml) && t.box && within(t.box, s.box));
  const behind = shapes
    .filter((s) => s.box && !s.text.trim() && s !== label && within(label.box, s.box) && !holdsTable(s))
    .sort((a, b) => a.box.w * a.box.h - b.box.w * b.box.h)[0];
  const area = behind?.box ?? label.box;
  let body = tree[1];
  for (const s of shapes) {
    if (s.box && within(s.box, area)) body = body.replace(s.xml, "");
  }
  return xml.replace(tree[1], () => body);
}

/* ------------------------------------------------------------ the stamp */

function slideSize(files) {
  const pres = files["ppt/presentation.xml"] ? strFromU8(files["ppt/presentation.xml"]) : "";
  const m = /<p:sldSz\b[^>]*\bcx="(\d+)"[^>]*\bcy="(\d+)"/.exec(pres);
  return m ? { cx: Number(m[1]), cy: Number(m[2]) } : { cx: 12192000, cy: 6858000 };
}

/* A DRAFT box in the cover's top right corner. The one thing this module adds
   rather than fills, because a draft has to say so where a client would see
   it first, and the template has no placeholder for being unfinished. Its own
   colours and size, so nothing in the template is restyled to make room. */
export function stamp(xml, text, { cx }) {
  const ids = [...xml.matchAll(/<p:cNvPr\b[^>]*\bid="(\d+)"/g)].map((m) => Number(m[1]));
  const id = Math.max(0, ...ids) + 1;
  const w = 2286000;
  const h = 457200;
  const x = Math.max(0, cx - w - 228600);
  const y = 228600;
  const shape = `<p:sp><p:nvSpPr><p:cNvPr id="${id}" name="Draft stamp"/><p:cNvSpPr txBox="1"/><p:nvPr/></p:nvSpPr>`
    + `<p:spPr><a:xfrm><a:off x="${x}" y="${y}"/><a:ext cx="${w}" cy="${h}"/></a:xfrm>`
    + `<a:prstGeom prst="rect"><a:avLst/></a:prstGeom><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill>`
    + `<a:ln w="28575"><a:solidFill><a:srgbClr val="9C0006"/></a:solidFill></a:ln></p:spPr>`
    + `<p:txBody><a:bodyPr wrap="square" lIns="91440" tIns="45720" rIns="91440" bIns="45720" anchor="ctr"/><a:lstStyle/>`
    + `<a:p><a:pPr algn="ctr"/><a:r><a:rPr lang="en-US" sz="2000" b="1"><a:solidFill><a:srgbClr val="9C0006"/></a:solidFill></a:rPr>`
    + `<a:t>${escapeXml(text)}</a:t></a:r></a:p></p:txBody></p:sp>`;
  return xml.replace(/<\/p:spTree>/, `${shape}</p:spTree>`);
}

/** Reads the template's own text back, for working out what a slide holds. */
export function readDeck(templateBytes) {
  const files = unzipSync(new Uint8Array(templateBytes));
  const order = deckOrder(files) ?? slideNumbers(files);
  return order.filter((n) => files[slidePath(n)]).map((n) => ({
    slide: n,
    xml: strFromU8(files[slidePath(n)]),
  }));
}
